import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { CdkTableModule } from '@angular/cdk/table';
import { UsersDataSource } from './users.datasource';
import { UsersService } from './users.service';
import { HeaderComponent } from '../shared/header.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EditUserComponent } from './edit-user.component';
import { ConfirmComponent } from '../shared/confirm.modal';
import { ToastrService } from 'ngx-toastr';
import { UserDto } from '@oracychampions/api-interfaces';
import { AuthService } from '../auth/auth.service';

@Component({
  selector: 'oc-users',
  standalone: true,
  imports: [CommonModule, RouterModule, CdkTableModule, HeaderComponent],
  template: `
    <div class="container-fluid page">
      <oc-header headerTitle="Users"></oc-header>
      <cdk-table #table [dataSource]="dataSource!" class="oc-table">
        <ng-container cdkColumnDef="firstName">
          <cdk-header-cell *cdkHeaderCellDef class="oc-table-header-cell"> First Name </cdk-header-cell>
          <cdk-cell *cdkCellDef="let row" class="oc-table-cell">
            {{ row.firstName }}
          </cdk-cell>
        </ng-container>

        <ng-container cdkColumnDef="lastName">
          <cdk-header-cell *cdkHeaderCellDef class="oc-table-header-cell"> Last Name </cdk-header-cell>
          <cdk-cell *cdkCellDef="let row" class="oc-table-cell">
            {{ row.lastName }}
          </cdk-cell>
        </ng-container>

        <ng-container cdkColumnDef="role">
          <cdk-header-cell *cdkHeaderCellDef class="oc-table-header-cell"> Role </cdk-header-cell>
          <cdk-cell *cdkCellDef="let row" class="oc-table-cell">
            <span *ngIf="row.role === 0">Admin</span>
            <span *ngIf="row.role === 1">Teacher</span>
          </cdk-cell>
        </ng-container>

        <ng-container cdkColumnDef="school">
          <cdk-header-cell *cdkHeaderCellDef class="oc-table-header-cell"> School </cdk-header-cell>
          <cdk-cell *cdkCellDef="let row" class="oc-table-cell">
            {{ row.school.name }}
          </cdk-cell>
        </ng-container>

        <ng-container cdkColumnDef="createDate">
          <cdk-header-cell *cdkHeaderCellDef class="oc-table-header-cell"> Created </cdk-header-cell>
          <cdk-cell *cdkCellDef="let row" class="oc-table-cell">
            {{ row.createDate | date }}
          </cdk-cell>
        </ng-container>

        <ng-container cdkColumnDef="actions">
          <cdk-header-cell *cdkHeaderCellDef class="oc-table-header-cell"> &nbsp; </cdk-header-cell>
          <cdk-cell *cdkCellDef="let row" class="oc-table-cell">
            <a (click)="editUser(row)">Edit</a>&nbsp;<a (click)="deleteUser(row)">Delete</a>
          </cdk-cell>
        </ng-container>

        <cdk-header-row *cdkHeaderRowDef="displayedColumns" class="oc-table-header-row"></cdk-header-row>
        <cdk-row *cdkRowDef="let row; columns: displayedColumns" class="oc-table-row"></cdk-row>
      </cdk-table>
    </div>
  `,
  styles: `
    
  `,
})
export class UsersComponent {
  private usersService = inject(UsersService);
  private modalService = inject(NgbModal);
  private toastr = inject(ToastrService);
  private auth = inject(AuthService);
  private route = inject(ActivatedRoute);

  private schoolId: string | null = null;

  dataSource: UsersDataSource | null = null;
  displayedColumns = ['firstName', 'lastName', 'role', 'school', 'createDate', 'actions'];

  constructor() {
    if (this.route.snapshot.data['scopeToSchool']) {
      this.schoolId = this.auth.auth$.getValue()?.user.schoolId;
      this.displayedColumns = ['firstName', 'lastName', 'role', 'createDate', 'actions'];
    }
    this.dataSource = new UsersDataSource(this.usersService, this.schoolId);
  }

  addUser() {
    const modalRef = this.modalService.open(EditUserComponent, { fullscreen: true });
    modalRef.closed.subscribe(() => {
      this.usersService.reloadUsers();
    });
  }

  editUser(user: UserDto) {
    const modalRef = this.modalService.open(EditUserComponent, { fullscreen: true });
    modalRef.componentInstance.id = user.id;
    modalRef.componentInstance.model = {
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role,
      superadmin: user.superadmin,
    };
    modalRef.closed.subscribe(() => {
      this.usersService.reloadUsers();
    });
  }

  deleteUser(user: UserDto) {
    const modalRef = this.modalService.open(ConfirmComponent);
    modalRef.closed.subscribe((confirmed) => {
      if (confirmed) {
        this.usersService.deleteUser(user.id).subscribe(() => {
          this.toastr.success('User deleted');
          this.usersService.reloadUsers();
        });
      }
    });
  }
}
